import firebase from "firebase/compat/app";
import "firebase/compat/messaging";
import "firebase/compat/remote-config";
import { AccountService } from "@services";
import utils from "@utils";
import { APP_ENV } from "@constants/index";
import { ENV_OPTIONS } from "@constants";

const debugMode = false;

const debugMsg = message => {
	if (debugMode) {
		// eslint-disable-next-line no-console
		console.log(message);
	}
};

// Register service worker required for notifications
if ("serviceWorker" in navigator) {
	navigator.serviceWorker
		.register("/firebase-messaging-sw.js")
		.then(registration => {
			debugMsg("Registration successful, scope is:", registration.scope);
		})
		.catch(err => {
			debugMsg("Service worker registration failed, error:", err);
		});
}

const { firebaseConfig } = ENV_OPTIONS;

const vapidKey =
	"BEUSONKhCNZOSTU8zJUYOY7gg1cq0SkSwiZF_gNCdl2HU-pWumkYPocNoBnT0oX1VDmYCbSiioP4gmlEYzHuwkE";

const firebaseApp = firebase.initializeApp(firebaseConfig);

// Remote config setup
const remoteConfig = firebase.remoteConfig(firebaseApp);
// One hour cache time for remote values
remoteConfig.settings.minimumFetchIntervalMillis = 3600000;
remoteConfig.defaultConfig = {
	/* eslint-disable camelcase */
	error_report_url: "",
	error_debug_enabled: false,
	network_debug_enabled: false,
	/* eslint-enable camelcase */
};

// Update account with Instance ID token
const sendUpdatedToken = token => {
	AccountService.updateDeviceToken(token)
		.then(() => {
			debugMsg("Sent device token to server.");
		})
		.catch(err => {
			debugMsg(err);
		});
};

let messaging = null;
if (firebase.messaging.isSupported()) {
	// must be supported by browser
	messaging = firebaseApp.messaging();

	// Handle incoming messages. Called when:
	// - a message is received while the app has focus
	// - the user clicks on an app notification created by a service worker
	//   `messaging.setBackgroundMessageHandler` handler.
	messaging.onMessage(payload => {
		document.dispatchEvent(new CustomEvent("notificationReceived", { detail: payload }));
	});
}

const requestPermission = async () => {
	if (Notification && messaging) {
		// If the user blocked notifications permission, it can only enabled via browser
		if (Notification.permission === "denied") {
			utils.notify({
				type: "error",
				message: "Push notifications are blocked. Please enable them in your browser.",
			});

			return false;
		}

		const permission = await Notification.requestPermission();
		if (permission === "granted") {
			debugMsg("Notification permission granted.");
			// eslint-disable-next-line no-use-before-define
			getToken();
			return true;
		}

		debugMsg("Unable to get permission to notify.");
		return false;
	}

	return false;
};

const getNotificationsEnabled = async () => {
	if ("Notification" in window && messaging) {
		// Get stored token from local browser
		const localPushToken = localStorage.getItem("pushToken");
		let pushTokenActive = false;
		if (localPushToken) {
			// Determine if local token registered with server
			const { data: regTokens } = await AccountService.getDeviceTokens();
			pushTokenActive = !!regTokens.find(({ DeviceToken }) => localPushToken === DeviceToken);
		}
		return Notification.permission === "granted" && pushTokenActive;
	}
	return false;
};

// Get Instance ID token. Initially this makes a network call, once retrieved
// subsequent calls to getToken will return from cache.
const getToken = () => {
	if (messaging) {
		messaging
			.getToken({ vapidKey })
			.then(currentToken => {
				if (currentToken) {
					sendUpdatedToken(currentToken);
					localStorage.setItem("pushToken", currentToken);
					debugMsg(`Token acquired: ${currentToken}`);
				} else {
					debugMsg("No Instance ID token available. Request permission to generate one.");
					// Show permission request.
					requestPermission();
				}
			})
			.catch(err => {
				debugMsg("An error occurred while retrieving token. ");
				debugMsg(err);
			});
	}
};

// Remove local token record and de-register token from server notifications
const removeToken = () => {
	if (messaging) {
		const localPushToken = localStorage.getItem("pushToken");
		if (localPushToken) {
			// Delete only after de-registered from server
			AccountService.deleteDeviceToken(localPushToken).then(() => {
				localStorage.removeItem("pushToken");
			});
		}
	}
};

/** Returns the value of a specified configuration key
 * @param {String} key The name of the parameter to retrieve a value for
 * @param {'bool'|'string'|'num'} dataType Optional parameter specifying the data type to return the value as
 * @returns The value for the given key
 */
const getConfigValue = async (key, dataType) => {
	if (typeof key !== "string") throw new Error("Key must be an string");

	/* To prevent reading undefined values, check to see if a default value is defined for the given key */
	if (typeof remoteConfig.defaultConfig[key] === "undefined") {
		throw new Error(`Remote config value for \`${key}\` doesn't have a local default value.`);
	}

	try {
		await remoteConfig.fetchAndActivate();

		switch (dataType) {
			case "bool":
				return remoteConfig.getBoolean(key);
			case "string":
				return remoteConfig.getString(key);
			case "num":
				return remoteConfig.getNumber(key);
			default:
				return remoteConfig.getValue(key);
		}
	} catch (e) {
		throw new Error("Error fetching remote config");
	}
};

// Get specific enviroment feature flag of key
const getEnvironmentFeatureFlag = async key => {
	const flagKey = `${key}_${APP_ENV}`;

	if (remoteConfig.defaultConfig[flagKey] === undefined) {
		remoteConfig.defaultConfig[flagKey] = remoteConfig.defaultConfig[key];
	}

	return getConfigValue(flagKey);
};

export default {
	messaging,
	getToken,
	removeToken,
	getNotificationsEnabled,
	requestPermission,
	getConfigValue,
	getEnvironmentFeatureFlag,
};
